<!-- ── Header ──────────────────────────────────────────────────────────────── -->
<div class="mcp-header p-d-flex p-jc-between p-ai-center">
    <div>
        <h4 class="p-m-0">MCP Connections</h4>
        <small class="p-text-secondary">
            Connect an AI assistant directly to MoveRight so it can look up jobs, run reports, and more.
        </small>
    </div>
    <button
        pButton
        label="New Connection"
        icon="pi pi-plus"
        class="p-button-sm"
        (click)="openWizard()"
    ></button>
</div>

<hr>

<!-- ── Token table ─────────────────────────────────────────────────────────── -->
<ng-container *ngIf="!tokensLoading; else loadingTokens">
    <ng-container *ngIf="tokens.length > 0; else noTokens">
        <p-table
            [value]="tokens"
            styleClass="p-datatable-sm"
            [responsive]="true"
        >
            <ng-template pTemplate="header">
                <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Scope</th>
                    <th>Created</th>
                    <th>Last Used</th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-token>
                <tr>
                    <td>{{ getTokenName(token) }}</td>
                    <td>{{ getTokenProvider(token) }}</td>
                    <td>
                        <span class="p-tag" [ngClass]="{
                            'p-tag-info': getTokenMeta(token)?.scope === 'reports',
                            'p-tag-success': getTokenMeta(token)?.scope === 'read',
                            'p-tag-warning': getTokenMeta(token)?.scope === 'write',
                            'p-tag-danger': getTokenMeta(token)?.scope === 'admin'
                        }">{{ getTokenScope(token) }}</span>
                    </td>
                    <td>{{ formatDate(token.createdAt) }}</td>
                    <td>{{ formatDate(token.lastUsed) }}</td>
                    <td>
                        <button
                            pButton
                            icon="pi pi-trash"
                            class="p-button-danger p-button-text p-button-sm"
                            pTooltip="Revoke connection"
                            (click)="promptRevoke(token)"
                        ></button>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </ng-container>
    <ng-template #noTokens>
        <p class="p-text-secondary p-text-center">
            No MCP connections yet. Click "New Connection" to get started.
        </p>
    </ng-template>
</ng-container>
<ng-template #loadingTokens>
    <p-skeleton height="3rem" styleClass="p-mb-2"></p-skeleton>
    <p-skeleton height="3rem"></p-skeleton>
</ng-template>

<!-- ══ Wizard Dialog ══════════════════════════════════════════════════════════ -->
<p-dialog
    [header]="'New MCP Connection — ' + wizardStepLabel"
    [(visible)]="showWizard"
    [modal]="true"
    [closable]="true"
    (onHide)="closeWizard()"
    [style]="{ width: '600px' }"
    [breakpoints]="{ '768px': '95vw' }"
>

    <!-- Step indicator -->
    <div class="wizard-steps p-d-flex p-jc-center p-mb-4">
        <span
            *ngFor="let step of wizardSteps()"
            class="step-dot"
            [class.active]="wizardStep === step"
            [class.done]="wizardStep > step"
        ></span>
    </div>

    <!-- ════════════════════════════════════════════════════════════════════
         Step 1: Choose assistant + name
    ═════════════════════════════════════════════════════════════════════ -->
    <ng-container *ngIf="wizardStep === 1">
        <p class="p-mb-3 p-text-secondary">
            Choose your AI assistant. The connection name is pre-filled — edit it if you like.
        </p>

        <div class="provider-grid p-mb-4">
            <div
                *ngFor="let provider of providers"
                class="provider-card"
                [class.selected]="selectedProvider?.id === provider.id"
                (click)="selectProvider(provider)"
                role="button"
                tabindex="0"
                (keyup.enter)="selectProvider(provider)"
            >
                <i [class]="provider.icon + ' provider-icon'"></i>
                <span class="provider-label">{{ provider.label }}</span>
                <small class="provider-desc p-text-secondary">{{ provider.description }}</small>
            </div>
        </div>

        <div class="p-fluid">
            <div class="p-field">
                <label for="mcp-token-name">Connection name</label>
                <input
                    id="mcp-token-name"
                    pInputText
                    [(ngModel)]="tokenName"
                    placeholder="e.g. Claude Desktop — Work"
                    maxlength="80"
                />
            </div>
        </div>
    </ng-container>

    <!-- ════════════════════════════════════════════════════════════════════
         Step 2: Zone + role
    ═════════════════════════════════════════════════════════════════════ -->
    <ng-container *ngIf="wizardStep === 2">
        <div class="p-field">
            <label>Default zone</label>
            <div class="zone-info">
                <i class="pi pi-map-marker zone-info-icon"></i>
                <span class="zone-info-name">{{ currentZoneName || 'Current zone' }}</span>
            </div>
            <small class="p-text-secondary">
                Every tool call requires a <code>zoneId</code>. Ask the AI to run
                <code>available_zones</code> first to discover your zones, then pass the
                <code>zoneId</code> to each subsequent call.
            </small>
        </div>

        <!-- Role dropdown — only shown when the zone has more than one role -->
        <div class="p-field p-fluid" *ngIf="multipleRoles">
            <label for="mcp-role">Role</label>
            <p-dropdown
                inputId="mcp-role"
                [options]="currentZoneRoles"
                [(ngModel)]="selectedRoleId"
                optionLabel="name"
                optionValue="id"
                placeholder="Select role"
            ></p-dropdown>
            <small class="p-text-secondary">
                The role determines which features the AI can see within the zone.
            </small>
        </div>
    </ng-container>

    <!-- ════════════════════════════════════════════════════════════════════
         Step 3: Permission scope
    ═════════════════════════════════════════════════════════════════════ -->
    <ng-container *ngIf="wizardStep === 3">
        <p class="p-mb-3 p-text-secondary">
            Choose how much access this connection has. You can always revoke it and create a new one.
        </p>

        <div class="scope-options p-mb-3">
            <div
                *ngFor="let scope of scopes"
                class="scope-option"
                [class.selected]="selectedScope === scope.value"
                [class.scope-admin]="scope.value === 'admin'"
                (click)="selectedScope = scope.value"
                role="radio"
                [attr.aria-checked]="selectedScope === scope.value"
                tabindex="0"
                (keyup.enter)="selectedScope = scope.value"
            >
                <span class="scope-label">{{ scope.label }}</span>
                <small class="p-text-secondary">{{ scope.description }}</small>
            </div>
        </div>

        <p-message
            *ngIf="isAdminScope()"
            severity="warn"
            text="Admin scope grants full MCP access including writes. Only use this if the AI needs to make changes."
            styleClass="p-mb-2"
        ></p-message>
    </ng-container>

    <!-- ════════════════════════════════════════════════════════════════════
         Step 4: Install instructions
    ═════════════════════════════════════════════════════════════════════ -->
    <ng-container *ngIf="wizardStep === 4">
        <p-message
            severity="warn"
            text="Copy your MCP URL now — it cannot be shown again after you close this dialog."
            styleClass="p-mb-4"
        ></p-message>

        <!-- URL copy row -->
        <div class="p-field p-mb-4">
            <label class="install-label">Your MCP URL</label>
            <div class="token-url-row">
                <input
                    pInputText
                    [value]="getMcpUrl()"
                    readonly
                    class="token-url-input"
                />
                <button
                    pButton
                    label="Copy URL"
                    icon="pi pi-copy"
                    class="p-button-secondary p-button-sm"
                    (click)="copyMcpUrl()"
                ></button>
            </div>
        </div>

        <!-- Numbered install steps -->
        <div class="install-steps">
            <div
                *ngFor="let step of getInstallSteps(); let i = index"
                class="install-step"
            >
                <div class="install-step-number">{{ i + 1 }}</div>
                <div class="install-step-content">
                    <div class="install-step-heading">{{ step.heading }}</div>
                    <p class="install-step-body">{{ step.body }}</p>
                    <div *ngIf="step.code" class="install-code-block">
                        <pre class="install-code">{{ step.code }}</pre>
                        <div class="install-code-footer">
                            <button
                                pButton
                                icon="pi pi-copy"
                                label="Copy"
                                class="p-button-text p-button-sm"
                                (click)="copyInstallConfig()"
                            ></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ng-container>

    <!-- ── Single footer for all steps — must be a direct p-dialog child ── -->
    <ng-template pTemplate="footer">
        <!-- Step 1 -->
        <div *ngIf="wizardStep === 1" class="p-d-flex p-jc-end">
            <button
                pButton
                label="Next"
                icon="pi pi-arrow-right"
                iconPos="right"
                [disabled]="!canAdvanceStep1()"
                (click)="goToStep2()"
            ></button>
        </div>
        <!-- Step 2 -->
        <div *ngIf="wizardStep === 2" class="p-d-flex p-jc-between">
            <button
                pButton
                label="Back"
                icon="pi pi-arrow-left"
                class="p-button-text"
                (click)="wizardStep = 1"
            ></button>
            <button
                pButton
                label="Next"
                icon="pi pi-arrow-right"
                iconPos="right"
                [disabled]="!canAdvanceStep2()"
                (click)="goToStep3()"
            ></button>
        </div>
        <!-- Step 3 -->
        <div *ngIf="wizardStep === 3" class="p-d-flex p-jc-between">
            <button
                pButton
                label="Back"
                icon="pi pi-arrow-left"
                class="p-button-text"
                (click)="wizardStep = 2"
            ></button>
            <button
                pButton
                label="Create Connection"
                icon="pi pi-check"
                [disabled]="!canCreate() || creatingToken"
                [loading]="creatingToken"
                (click)="createToken()"
            ></button>
        </div>
        <!-- Step 4 -->
        <div *ngIf="wizardStep === 4" class="p-d-flex p-jc-end">
            <button
                pButton
                label="Done"
                icon="pi pi-check"
                (click)="closeWizard()"
            ></button>
        </div>
    </ng-template>

</p-dialog>

<!-- ══ Revoke Confirmation Dialog ═══════════════════════════════════════════ -->
<p-dialog
    header="Revoke MCP Connection"
    [(visible)]="showRevokeConfirm"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '380px' }"
>
    <p>
        Revoke <strong>{{ tokenToRevoke ? getTokenName(tokenToRevoke) : '' }}</strong>?
        Any AI assistant using this connection will immediately lose access.
    </p>

    <ng-template pTemplate="footer">
        <button
            pButton
            label="Cancel"
            class="p-button-text"
            (click)="cancelRevoke()"
        ></button>
        <button
            pButton
            label="Revoke"
            icon="pi pi-trash"
            class="p-button-danger"
            [loading]="revoking"
            (click)="confirmRevoke()"
        ></button>
    </ng-template>
</p-dialog>
